import { useEffect, useState, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Table } from "react-bootstrap";
import DashboardStats from "../../../../common/Dashboard/DashboardStats";
import DashboardStatsSkeleton from "../../../../common/Dashboard/DashboardStatsSkeleton";
import TableSkeleton from "../../../common/TableSkeleton";
import TableError from "../../../common/TableError";
import TablePagination from "../../pagination/TablePagination";
import { useGetClassroomReport } from "../../../../../hooks/teacher/classroom/useGetClassroomReport";
import { ITEMS_PER_PAGE } from "../../../../../constant/constant";
import RefreshButtonTable from "../../../../common/RefreshButtonTable";
import classRoomImg from "../../../../../assets/img/teacherDashboard/Classroom.png";
import "../../../../../common/styles/table.css";
import BackButton from "../../Goback/GoBack";
import { useNavigate } from "react-router-dom";

/**
 * ClassroomReport - Component to display classroom performance metrics and student list
 * @param {"school-admin" | "teacher"} type - Type of user (teacher or school-admin)
 */
const ClassroomReport = ({ type }) => {
  const { id: classroomId } = useParams();
  const location = useLocation();
  const classroomNameFromState =
    location.state?.classroomName || "Classroom Report";

  const [currentPage, setCurrentPage] = useState(1);

  const { loading, error, report, getClassroomReport } =
    useGetClassroomReport();
  const navigate = useNavigate();

  const fetchReport = useCallback(
    (page) => {
      if (classroomId) {
        getClassroomReport({
          classroomId,
          page,
          per_page: ITEMS_PER_PAGE,
        });
      }
    },
    [classroomId],
  );

  useEffect(() => {
    fetchReport(currentPage);
  }, [currentPage]);

  const handleRefresh = () => {
    fetchReport(currentPage);
  };

  const classroomDetail = report?.classroom;
  const students = report?.students?.data || [];
  const totalPages = report?.students?.last_page || 1;

  // Check if initial load (no data yet)
  const isInitialLoad = report === undefined || report === null;

  // Stats data for DashboardStats component
  const stats = [
    {
      label: "Total Students",
      value:
        loading && isInitialLoad ? "..." : classroomDetail?.total_students || 0,
      icon: "fa-users",
      colorClass: "dashboard-stat-card-blue",
    },
    {
      label: "Total Lessons",
      value:
        loading && isInitialLoad ? "..." : classroomDetail?.total_lessons || 0,
      icon: "fa-book-open",
      colorClass: "dashboard-stat-card-peach",
    },
    {
      label: "Avg. Score",
      value:
        loading && isInitialLoad ? "..." : `${classroomDetail?.avg_score || 0}`,
      icon: "fa-check-circle",
      colorClass: "dashboard-stat-card-purple",
    },
    {
      label: "Total Quests",
      value:
        loading && isInitialLoad ? "..." : classroomDetail?.total_quests || 0,
      icon: "fa-clipboard-list",
      colorClass: "dashboard-stat-card-green",
    },
  ];

  // Show skeleton on initial load
  if (loading && isInitialLoad) {
    return (
      <div className="classroom-cnt">
        <div className="classroom-container">
          <div className="pb-0">
            <BackButton />
          </div>
          <img src={classRoomImg} className="img" alt="Classroom" />
          <h2 className="text">{classroomNameFromState}</h2>
        </div>

        <div className="pt-2">
          <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
        </div>
        <DashboardStatsSkeleton count={4} />
        <TableSkeleton rows={ITEMS_PER_PAGE} columns={5} />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="classroom-cnt">
        <div className="classroom-container">
          <div className="pb-0">
            <BackButton />
          </div>
          <img src={classRoomImg} className="img" alt="Classroom" />
          <h2 className="text">{classroomNameFromState}</h2>
        </div>

        <div className="pt-2">
          <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
        </div>
        <TableError
          error={error}
          handleRefresh={handleRefresh}
          loading={loading}
        />
      </div>
    );
  }

  const handleStudentClick = (studentId) => {
    console.log("WORKING ")
    if (type === "teacher") {
      navigate(`/teacher/student/${studentId}`);
    } else {
      navigate(`/school-admin/school/report/students/${studentId}`);
    }
  }

  return (
    <div className="classroom-cnt">
      <div className="classroom-container">
        <div className="pb-0">
          <BackButton />
        </div>
        <img src={classRoomImg} className="img" alt="Classroom" />
        <h2 className="text">
          {classroomDetail?.name || classroomNameFromState}
        </h2>
      </div>

      <div className="pt-2">
        <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
      </div>

      {/* Stats Cards using DashboardStats */}
      <DashboardStats stats={stats} />

      {/* Students Table */}
      {students.length === 0 ? (
        <div className="alert alert-info mt-3" role="alert">
          No students currently enrolled or tracking data in this classroom.
        </div>
      ) : (
        <div className="custom-table-container mt-3">
          <div className="custom-table-wrapper">
            <Table hover className="custom-table">
              <thead className="custom-table-head">
                <tr>
                  <th>Name</th>
                  <th>Contact Info</th>
                  <th className="text-center">Lessons</th>
                  <th className="text-center">Avg Score</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {students.map((studentItem, index) => (
                  <tr key={studentItem.id || index}
                    onClick={() => handleStudentClick(studentItem.id)}
                    style={{ cursor: "pointer" }}>
                    <td>{studentItem.name || "-"}</td>
                    <td>
                      <div className="d-flex flex-column text-muted small">
                        <span>{studentItem.username || "-"}</span>
                        <span>{studentItem.email || "-"}</span>
                      </div>
                    </td>
                    <td className="text-center">
                      {studentItem.lessons || "-"}
                    </td>
                    <td className="text-center">
                      {studentItem.avg_score ?? 0}
                    </td>
                    <td className="text-center">
                      {studentItem.status === "complete" ? (
                        <span className="badge bg-success">Complete</span>
                      ) : studentItem.status ? (
                        <span
                          className="badge bg-secondary"
                          style={{ textTransform: "capitalize" }}
                        >
                          {studentItem.status}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <TablePagination
            totalPage={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
};

export default ClassroomReport;
